import React, { useState, useEffect, useRef } from "react";
import BackButton from "../../../Components/Buttons/Variants/BackButton";
import Text from "../../../Components/Text";
import Button from "../../../Components/Buttons/Button";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { dbEvents } from "../../../db/dbEvents";
import TIcon from "../../../Components/TIcon";
import { getOrdersByStatus, updateOrderStatus } from "../../../Model/Orders";

const OrderDisplay = () => {
    const [openOrders, setOpenOrders] = useState(true);
    const [displayOrders, setDisplayOrders] = useState([]);
    const [swipingIndex, setSwipingIndex] = useState(null);
    const [startTouch, setStartTouch] = useState(0);
    const [currentTouch, setCurrentTouch] = useState(0);
    const openOrdersRef = useRef(openOrders);
    const [, setNow] = useState(Date.now());

    useEffect(() => {
        openOrdersRef.current = openOrders;
    }, [openOrders]);

    useEffect(() => {
        fetchOrders();
        const handleChange = (e) => {
            if (["orders", "sync_orders"].includes(e.detail.store)) {
                getOrdersByState(openOrdersRef.current); // <- immer aktuell
            }
        };
        dbEvents.addEventListener("dbChange", handleChange);

        return () => {
            dbEvents.removeEventListener("dbChange", handleChange);
        };
    }, []);

    // Timer 
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const fetchOrders = async (ref = null) => {
        if (ref == null) {
            getOrdersByState(openOrders)
        } else {
            getOrdersByState(ref)
        }
    };

    const getOrdersByState = async (option) => {
        let orders = [];
        if (option) {
            orders = await getOrdersByStatus('PENDING');
        } else {
            orders = await getOrdersByStatus('COMPLETE');
            orders.reverse();
        }
        setDisplayOrders(orders);
    }

    useEffect(() => {
        setDisplayOrders([])
        fetchOrders();
        openOrdersRef.current = openOrders;
    }, [openOrders]);

    const getBackgroundColor = (orderType) => {
        if (!openOrders) {
            return "bg-gray-400";
        }
        switch (orderType) {
            case "ON_SPOT":
                return "bg-[#3B7DDB]";
            case "TAKE_AWAY":
                return "bg-[#37A35C]";
            default:
                return "bg-gray-400";
        }
    };

    const getButtonColor = (orderType) => {
        if (!openOrders) {
            return "bg-gray-600";
        }
        switch (orderType) {
            case "ON_SPOT":
                return "bg-[#2C5EA4]";
            case "TAKE_AWAY":
                return "bg-[#2C824A]";
            default:
                return "bg-gray-600";
        }
    };

    const handleTouchStart = (e, index) => {
        if (!openOrders) return;
        setSwipingIndex(index);
        setStartTouch(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
        if (!openOrders) return;
        setCurrentTouch(e.touches[0].clientX);
    };

    const handleTouchEnd = (e, order) => {
        if (!openOrders) return;
        if (currentTouch - startTouch > 100) {
            completeOrder(order);
        }
        setStartTouch(0);
        setCurrentTouch(0);
        setSwipingIndex(null);
    };

    const completeOrder = async (order) => {
        await updateOrderStatus(order.id, "COMPLETE");
        await fetchOrders();
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };

    const convertDuration = (duration) => {
        if (duration == null) { return }
        const regex = /PT(\d+H)?(\d+M)?(\d+(\.\d+)S)?/;
        const match = duration.match(regex);
        const minutes = match[2] ? parseInt(match[2].replace('M', '')) : 0;
        const seconds = match[3] ? parseFloat(match[3].replace('S', '')) : 0;

        const totalSeconds = Math.floor(seconds);
        const formattedSeconds = totalSeconds < 10 ? `0${totalSeconds}` : totalSeconds;
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

        return `${formattedMinutes}:${formattedSeconds}`;
    };

    const timeLabel = (time) => {
        const durationInSeconds = (new Date() - new Date(time)) / 1000;
        const displayTime = formatTime(durationInSeconds);
        const minutes = Math.floor((time % 3600) / 60);
        const isOverFifteenMinutes = minutes > 15;
        const isOverTwentyMinutes = minutes > 20;
        return (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${isOverTwentyMinutes ? 'bg-[#791F1F]' : isOverFifteenMinutes ? 'bg-[#854F0B]' : 'bg-[rgba(0,0,0,0.25)]'}`}>
                <TIcon name={isOverTwentyMinutes ? 'alert-triangle' : 'clock'} size={20} color="white" />
                <Text type="itemmain" classNames={"text-white"}>{displayTime}</Text>
            </div>
        )
    }

    const stoppedTimeLabel = (time) => {
        const convertedTime = convertDuration(time)
        return (
            <div className={`flex items-center gap-2 px-3 py-1 rounded-lg bg-[rgba(0,0,0,0.25)]`}>
                <TIcon name="stopwatch" size={20} color="white" />
                <Text type="itemmain" classNames={"text-white"}>{convertedTime}</Text>
            </div>
        )
    }

    const orderCard = (order, index) => (
        <div
            key={index}
            className={`relative p-4 rounded-lg ${getBackgroundColor(order.type)} text-white`}
            onTouchStart={(e) => handleTouchStart(e, index)}
            onTouchMove={handleTouchMove}
            onTouchEnd={(e) => handleTouchEnd(e, order)}
            style={{
                transform: `translateX(${swipingIndex === index ? Math.max(currentTouch - startTouch, 0) : 0}px)`,
                transition: "transform 0.3s ease-out",
            }}
        >
            <div className="flex justify-between h-full">
                <div className="mt-3">
                    <ul>
                        {/* normale Order-Produkte */}
                        {order?.orderProducts?.map((product, idx) => (
                            <li key={`product-${idx}`} className="flex flex-row py-1 gap-3 items-center">
                                <Text type="maintitle">
                                    {product.count}x {product.name}
                                </Text>

                                {product.extras?.length > 0 && (
                                    <Text type="itemmain">
                                        {product.extras.map(extra => extra.name).join(", ")}
                                    </Text>
                                )}
                            </li>
                        ))}

                        {order.coupons?.map((offer, offerIdx) =>
                            offer.offerSnapshotProducts.map((product, productIdx) => (
                                <li
                                    key={`offer-${offerIdx}-product-${productIdx}`}
                                    className="flex flex-row py-1 gap-3 items-center"
                                >
                                    <Text type="maintitle">
                                        {product.count}x {product.name}
                                    </Text>
                                </li>
                            ))
                        )}
                        {order.comment && (
                            <li className="flex flex-row py-1 gap-3 items-center">
                                <Text type="itemmain">Kommentar: {order.comment}</Text>
                            </li>
                        )}
                        {order.preorderName && (
                            <li className="flex flex-row py-1 gap-3 items-center">
                                <Text type="itemmain">Vorbesteller: {order.preorderName}</Text>
                            </li>
                        )}
                        {order.status === "PENDING" && (
                            <Button classNames="px-4 py-2 mt-4 text-white"
                                buttonColor={getButtonColor(order.type)}
                                borderStyle="rounded-lg"
                                onClickAction={() => completeOrder(order)} >
                                <Text type="buttontext" classNames="text-white">Abschliessen</Text>
                            </Button>
                        )}
                    </ul>
                </div>
                <div className="py-2 h-full flex flex-col justify-between gap-2">
                    {order.status === "PENDING" ? (
                        timeLabel(order.startTime)
                    ) : (
                        stoppedTimeLabel(order.duration)
                    )}

                    {order.status === "PENDING" && (
                        <div className="flex flex-col">
                            <Text type="orderitem" classNames={"text-white opacity-50"}>Wischen &gt;&gt;</Text>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const mainView = () => (
        <div className="w-full bg-black border-black border flex flex-col divide-y-2 divide-white">
            {/* Header */}
            <div className="w-full flex flex-row px-5 py-2 gap-3 items-center">
                <div className="flex flex-row justify-start">
                    <BackButton />
                </div>
                <Text type="maintitle" classNames="flex justify-center text-white">
                    Bestellungen
                </Text>
                <ToggleSwitch
                    toggleObjects={[
                        { text: "Offen", selectedStyle: "bg-blue-500 text-white", unselectedStyle: "bg-black text-white", onClickAction: () => setOpenOrders(true) },
                        { text: "Abgeschlossen", selectedStyle: "bg-blue-500 text-white", unselectedStyle: "bg-black text-white", onClickAction: () => setOpenOrders(false) }
                    ]}
                    backgroundClasses=""
                    buttonClasses="w-fit px-5 rounded-lg"
                />
            </div>
            {/* Bestellkarten */}
            <div className="p-5 h-full overflow-y-auto">
                {displayOrders.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                        {displayOrders.map((order, index) => (
                            orderCard(order, index)
                        ))}
                    </div>
                ) : (
                    <Text type="maintitle" classNames="text-center text-white">Keine Bestellungen vorhanden</Text>
                )}
            </div>
        </div>
    );

    return mainView();
};

export default OrderDisplay;